/** [TBD] Every value here is dummy data — there is no workout API yet. */

export const WORKOUT_HEADER = {
  status: 'Day 13 | You’re On Track',
  headline: 'Ready for Today’s Workout?',
  focus: 'Upper Body Strength',
  duration: '45 min',
  exercises: '13 Exercises',
  /** Inside the member's artwork set — resolve with `memberArt`. */
  hero: 'app/workout.webp',
};

export type DayKind = 'workout' | 'rest';

export interface PlanDay {
  label: string;
  kind: DayKind;
  isToday: boolean;
}

/** Always 14 days — a window onto the plan, so the count does not change with progress. */
const STRIP_LENGTH = 14;
const TODAY_INDEX = 0;

export const PLAN_DAYS: readonly PlanDay[] = Array.from({ length: STRIP_LENGTH }, (_, index) => ({
  label: String(index + 5).padStart(2, '0'),
  // [TBD] Alternating is a stand-in. Which days are rest days comes from the plan.
  kind: index % 2 === 0 ? 'workout' : 'rest',
  isToday: index === TODAY_INDEX,
}));

export function planDayByLabel(label: string): PlanDay | undefined {
  return PLAN_DAYS.find((day) => day.label === label);
}

/** The strip starts at today, so a day's index is its offset in days from now. */
export function planDayDateLabel(label: string, today: Date = new Date()): string {
  const index = PLAN_DAYS.findIndex((day) => day.label === label);
  const offset = index === -1 ? 0 : index - TODAY_INDEX;
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
  const dayOfMonth = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  return `${String(dayOfMonth).padStart(2, '0')}${ordinalSuffix(dayOfMonth)} ${month}`;
}

function ordinalSuffix(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export const TODAY_SESSION = {
  title: 'Start Today’s Workout',
  subtitle: 'Upper Body Strength | 45 min',
};

/* ---- Rest day screen (reached from the Start card when the selected day is a rest day) ---- */

export interface RecoveryTip {
  id: string;
  emoji: string;
  title: string;
  body: string;
}

export const REST_DAY = {
  heading: 'Time to Recover',
  emoji: '🌱',
  intro:
    'Recovery is part of your fitness journey. A well-earned rest day helps your body repair and prepare for your next workout. Taking a break today supports better performance and long-term progress.',
  tipsTitle: 'Recovery Tips',
  tips: [
    {
      id: 'walk',
      emoji: '🚶',
      title: 'Take a light walk',
      body: 'Take a short walk or do gentle stretching to keep your body moving.',
    },
    {
      id: 'hydrate',
      emoji: '💧',
      title: 'Stay hydrated',
      body: 'Drink plenty of water to support recovery and overall health.',
    },
    {
      id: 'eat',
      emoji: '🥗',
      title: 'Eat nutritious meals',
      body: 'Choose balanced meals with protein, healthy carbs, and vegetables to fuel muscle repair.',
    },
    {
      id: 'sleep',
      emoji: '😴',
      title: 'Get enough sleep',
      body: 'Aim for 7–9 hours of sleep to help your body recover and recharge.',
    },
  ] as const satisfies readonly RecoveryTip[],
  friendlyTipLabel: 'Friendly tip',
  friendlyTip:
    "Rest isn't a setback, it's part of the plan. Recover today so you can perform your best tomorrow.",
  /** Inside the member's artwork set — resolve with `memberArt`. */
  illustration: 'app/rest-day.webp',
};

/* ---- Today's Workout session screen (reached from the Start card's play button) ---- */

export type SessionSectionId = 'warm-up' | 'workout' | 'cool-down';

export interface SessionExercise {
  id: string;
  name: string;
  detail: string;
  /** Strings: they carry their own units ("30s", "12 reps"). */
  sets: string;
  reps: string;
  rest: string;
  steps: readonly string[];
  friendlyTip: string;
  muscles: readonly string[];
}

export interface SessionSection {
  id: SessionSectionId;
  label: string;
  exercises: readonly SessionExercise[];
}

/** [TBD] One placeholder thumbnail for every exercise until real clips/stills exist. */
export const SESSION_EXERCISE_THUMB = '/questions/option.png';

/** [TBD] One sample clip for every exercise. It must be H.264 MP4 to play in the WebView. */
export const SESSION_EXERCISE_VIDEO = '/workout/exercise-demo.mp4';

export function findSessionExercise(
  id: string,
): { exercise: SessionExercise; section: SessionSection } | undefined {
  for (const section of SESSION_SECTIONS) {
    const exercise = section.exercises.find((entry) => entry.id === id);
    if (exercise !== undefined) return { exercise, section };
  }
  return undefined;
}

/** [TBD] `steps`, `friendlyTip` and `muscles` are placeholder copy, not from a real plan. */
export const SESSION_SECTIONS: readonly SessionSection[] = [
  {
    id: 'warm-up',
    label: 'Warm Up',
    exercises: [
      {
        id: 'wu-1',
        name: 'Hip Circles',
        detail: '2 set | 15 sec each side',
        sets: '2',
        reps: '15s',
        rest: '30s',
        steps: [
          'Stand with your feet shoulder-width apart and hands on your hips.',
          'Push your hips out to one side, then rotate them in a slow, controlled circle.',
          'Keep your upper body still and let the movement come from the hips.',
          'Do all reps one way, then circle the other way for the same count.',
        ],
        friendlyTip: 'Move slowly, never force it. Stop if anything feels sharp.',
        muscles: ['Hips', 'Lower Back', 'Core'],
      },
      {
        id: 'wu-2',
        name: 'Neck Rolls',
        detail: '1 set | 30 sec each side',
        sets: '1',
        reps: '30s',
        rest: '90s',
        steps: [
          'Sit or stand tall with your shoulders relaxed and down.',
          'Drop your chin gently toward your chest.',
          'Roll your head slowly toward one shoulder, then back to centre.',
          'Repeat to the other side, keeping the movement small and easy.',
        ],
        friendlyTip: 'Move slowly, never force it. Stop if anything feels sharp.',
        muscles: ['Neck', 'Upper Traps'],
      },
      {
        id: 'wu-3',
        name: 'Arm Circles',
        detail: '2 set | 20 reps',
        sets: '2',
        reps: '20',
        rest: '30s',
        steps: [
          'Stand with your feet shoulder-width apart, arms out to your sides at shoulder height.',
          'Draw small circles forward, keeping your arms straight.',
          'After your reps, reverse the direction for the same count.',
          'Keep your shoulders down and away from your ears throughout.',
        ],
        friendlyTip: 'Move slowly, never force it. Stop if anything feels sharp.',
        muscles: ['Shoulders', 'Upper Back'],
      },
      {
        id: 'wu-4',
        name: 'Cat–Cow Stretch',
        detail: '2 set | 10 reps',
        sets: '2',
        reps: '10',
        rest: '30s',
        steps: [
          'Start on all fours with wrists under shoulders and knees under hips.',
          'Inhale, drop your belly, and lift your chest and tailbone (cow).',
          'Exhale, round your spine toward the ceiling and tuck your chin (cat).',
          'Flow between the two shapes with your breath.',
        ],
        friendlyTip: 'Move slowly, never force it. Stop if anything feels sharp.',
        muscles: ['Spine', 'Core', 'Lower Back'],
      },
      {
        id: 'wu-5',
        name: 'Jumping Jacks',
        detail: '1 set | 40 sec',
        sets: '1',
        reps: '40s',
        rest: '60s',
        steps: [
          'Stand tall with your feet together and arms at your sides.',
          'Jump your feet out wide as you sweep your arms overhead.',
          'Jump back to the start in one smooth motion.',
          'Keep a steady rhythm and land softly through your knees.',
        ],
        friendlyTip: 'Move slowly, never force it. Stop if anything feels sharp.',
        muscles: ['Full Body', 'Calves', 'Shoulders'],
      },
    ],
  },
  {
    id: 'workout',
    label: 'Workout',
    exercises: [
      {
        id: 'wo-1',
        name: 'Push-Ups',
        detail: '3 set | 12 reps',
        sets: '3',
        reps: '12',
        rest: '60s',
        steps: [
          'Start in a plank with hands slightly wider than your shoulders.',
          'Keep your body in a straight line from head to heels.',
          'Lower your chest toward the floor, elbows at roughly 45° from your body.',
          'Press back up to the start without letting your hips sag.',
        ],
        friendlyTip: 'Keep your core tight the whole time. Drop to your knees if form slips.',
        muscles: ['Chest', 'Triceps', 'Shoulders'],
      },
      {
        id: 'wo-2',
        name: 'Dumbbell Rows',
        detail: '3 set | 10 each side',
        sets: '3',
        reps: '10',
        rest: '60s',
        steps: [
          'Place one knee and hand on a bench, the other foot on the floor.',
          'Hold a dumbbell in your free hand, arm hanging straight down.',
          'Pull the dumbbell to your hip, squeezing your shoulder blade in.',
          'Lower it slowly under control, then repeat before switching sides.',
        ],
        friendlyTip: 'Keep your back flat, not rounded. Lead with your elbow, not your hand.',
        muscles: ['Lats', 'Upper Back', 'Biceps'],
      },
      {
        id: 'wo-3',
        name: 'Shoulder Press',
        detail: '3 set | 12 reps',
        sets: '3',
        reps: '12',
        rest: '60s',
        steps: [
          'Sit or stand tall with a dumbbell in each hand at shoulder height.',
          'Keep your core braced and your ribs down.',
          'Press the weights straight overhead until your arms are almost locked.',
          'Lower back to shoulder height slowly, keeping control.',
        ],
        friendlyTip: 'Keep your core tight the whole time. Drop to your knees if form slips.',
        muscles: ['Shoulders', 'Triceps', 'Upper Chest'],
      },
      {
        id: 'wo-4',
        name: 'Bicep Curls',
        detail: '3 set | 15 reps',
        sets: '3',
        reps: '15',
        rest: '45s',
        steps: [
          'Stand tall with a dumbbell in each hand, palms facing forward.',
          'Keep your elbows pinned to your sides.',
          'Curl the weights up toward your shoulders, squeezing at the top.',
          'Lower slowly all the way down before the next rep.',
        ],
        friendlyTip: 'No swinging. If you have to rock your body, the weight is too heavy.',
        muscles: ['Biceps', 'Forearms'],
      },
      {
        id: 'wo-5',
        name: 'Tricep Dips',
        detail: '3 set | 12 reps',
        sets: '3',
        reps: '12',
        rest: '60s',
        steps: [
          'Sit on the edge of a bench, hands next to your hips, fingers forward.',
          'Slide your hips off the bench with your legs out in front.',
          'Bend your elbows straight back to lower your hips toward the floor.',
          'Press back up until your arms are straight.',
        ],
        friendlyTip: 'Keep your shoulders down, away from your ears. Stop if they pinch.',
        muscles: ['Triceps', 'Shoulders', 'Chest'],
      },
      {
        id: 'wo-6',
        name: 'Lateral Raises',
        detail: '3 set | 15 reps',
        sets: '3',
        reps: '15',
        rest: '45s',
        steps: [
          'Stand with a dumbbell in each hand at your sides, a slight bend in your elbows.',
          'Raise both arms out to the sides until they reach shoulder height.',
          'Lead with your elbows, keeping your wrists below them.',
          'Lower slowly under control.',
        ],
        friendlyTip: 'No swinging. If you have to rock your body, the weight is too heavy.',
        muscles: ['Side Delts', 'Upper Traps'],
      },
      {
        id: 'wo-7',
        name: 'Chest Fly',
        detail: '3 set | 12 reps',
        sets: '3',
        reps: '12',
        rest: '60s',
        steps: [
          'Lie on a bench with a dumbbell in each hand, arms above your chest.',
          'Keep a soft bend in your elbows throughout.',
          'Open your arms out to the sides in a wide arc until you feel a stretch.',
          'Bring the weights back together above your chest, squeezing your chest.',
        ],
        friendlyTip: 'Keep the bend in your elbows fixed. Do not press, just open and close.',
        muscles: ['Chest', 'Front Delts'],
      },
      {
        id: 'wo-8',
        name: 'Plank Hold',
        detail: '3 set | 45 sec',
        sets: '3',
        reps: '45s',
        rest: '45s',
        steps: [
          'Rest on your forearms and toes, elbows under your shoulders.',
          'Keep your body in a straight line from head to heels.',
          'Squeeze your glutes and brace your core.',
          'Breathe steadily and hold for time without letting your hips drop or pike.',
        ],
        friendlyTip: 'Quality over time. End the set the moment your hips start to sag.',
        muscles: ['Core', 'Shoulders', 'Glutes'],
      },
    ],
  },
  {
    id: 'cool-down',
    label: 'Cool Down',
    exercises: [
      {
        id: 'cd-1',
        name: 'Chest Stretch',
        detail: '1 set | 30 sec each side',
        sets: '1',
        reps: '30s',
        rest: '30s',
        steps: [
          'Stand next to a wall or door frame.',
          'Place your forearm on the surface with your elbow at shoulder height.',
          'Turn your body gently away until you feel a stretch across your chest.',
          'Hold, breathe, then switch sides.',
        ],
        friendlyTip: 'Ease into it. A stretch should feel long, never sharp.',
        muscles: ['Chest', 'Front Delts'],
      },
      {
        id: 'cd-2',
        name: 'Shoulder Stretch',
        detail: '1 set | 30 sec each side',
        sets: '1',
        reps: '30s',
        rest: '30s',
        steps: [
          'Bring one arm straight across your body at chest height.',
          'Use your other hand to gently draw it closer to your chest.',
          'Keep your shoulders down and relaxed.',
          'Hold, breathe, then switch sides.',
        ],
        friendlyTip: 'Ease into it. A stretch should feel long, never sharp.',
        muscles: ['Rear Delts', 'Upper Back'],
      },
      {
        id: 'cd-3',
        name: 'Triceps Stretch',
        detail: '1 set | 30 sec each side',
        sets: '1',
        reps: '30s',
        rest: '30s',
        steps: [
          'Raise one arm overhead and bend the elbow so your hand drops behind your neck.',
          'Use your other hand to gently press the bent elbow back and down.',
          'Keep your head tall, not pushed forward.',
          'Hold, breathe, then switch sides.',
        ],
        friendlyTip: 'Ease into it. A stretch should feel long, never sharp.',
        muscles: ['Triceps', 'Lats'],
      },
      {
        id: 'cd-4',
        name: 'Child’s Pose',
        detail: '1 set | 60 sec',
        sets: '1',
        reps: '60s',
        rest: '0s',
        steps: [
          'Kneel on the floor with your big toes together and knees wide.',
          'Sit your hips back toward your heels.',
          'Walk your hands forward and lower your chest toward the floor.',
          'Rest your forehead down and breathe slowly.',
        ],
        friendlyTip: 'Let your whole body go heavy. This one is about slowing down.',
        muscles: ['Lower Back', 'Hips', 'Shoulders'],
      },
      {
        id: 'cd-5',
        name: 'Deep Breathing',
        detail: '1 set | 60 sec',
        sets: '1',
        reps: '60s',
        rest: '0s',
        steps: [
          'Sit or lie down somewhere comfortable.',
          'Breathe in slowly through your nose for a count of four.',
          'Hold gently for a count of two.',
          'Breathe out through your mouth for a count of six, and repeat.',
        ],
        friendlyTip: 'If your mind wanders, just come back to the count. That is the exercise.',
        muscles: ['Diaphragm', 'Core'],
      },
    ],
  },
];

export interface WeightPoint {
  day: number;
  kg: number;
}

export const WEIGHT_SERIES: readonly WeightPoint[] = [
  { day: 1, kg: 88.2 },
  { day: 2, kg: 88.0 },
  { day: 3, kg: 87.6 },
  { day: 4, kg: 87.7 },
  { day: 5, kg: 87.2 },
  { day: 6, kg: 86.9 },
  { day: 7, kg: 86.4 },
  { day: 8, kg: 86.5 },
  { day: 9, kg: 86.0 },
  { day: 10, kg: 86.1 },
  { day: 11, kg: 85.4 },
  { day: 12, kg: 85.0 },
  { day: 13, kg: 84.6 },
  { day: 14, kg: 84.1 },
];

export type DayState = 'done' | 'rest' | 'today' | 'upcoming' | 'missed' | 'outside';

export interface MonthDay {
  date: number;
  state: DayState;
}

export const WEEKDAY_LABELS: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Fixed rest weekdays. 0 is Sunday. */
const REST_WEEKDAYS: readonly number[] = [0, 3];

/** [TBD] Every fourth workout day is marked missed until the API says which were done. */
const MISSED_EVERY = 4;

export function monthLabel(today: Date): string {
  return `${today.toLocaleString('en-US', { month: 'long' })} ${String(today.getFullYear())}`;
}

/** Always six rows, so the card does not change height between months. */
export function monthGrid(today: Date): MonthDay[] {
  const year = today.getFullYear();
  const month = today.getMonth();
  const currentDate = today.getDate();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  // getDay() is Sunday-first; shift so Monday is column 0.
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const cells: MonthDay[] = [];

  for (let i = 0; i < lead; i += 1) {
    cells.push({ date: daysInPrevMonth - lead + 1 + i, state: 'outside' });
  }

  for (let date = 1; date <= daysInMonth; date += 1) {
    cells.push({ date, state: stateFor(new Date(year, month, date), date, currentDate) });
  }

  let next = 1;
  while (cells.length < WEEKS_SHOWN * 7) {
    cells.push({ date: next, state: 'outside' });
    next += 1;
  }
  return cells;
}

const WEEKS_SHOWN = 6;

function stateFor(day: Date, date: number, currentDate: number): DayState {
  if (REST_WEEKDAYS.includes(day.getDay())) return 'rest';
  if (date === currentDate) return 'today';
  if (date > currentDate) return 'upcoming';
  return date % MISSED_EVERY === 0 ? 'missed' : 'done';
}

export const CALENDAR_LEGEND: readonly { state: DayState; label: string }[] = [
  { state: 'done', label: 'Workout done' },
  { state: 'rest', label: 'Rest Days' },
  { state: 'upcoming', label: 'Upcoming' },
  { state: 'missed', label: 'Missed' },
];

export const PHASE_INFO = {
  badge: 'Phase 1 of 27',
  daysLeft: '5 days left',
  headline: 'Right now, it’s all about your foundation.',
  body: 'This phase focuses on building baseline strength and getting your body used to consistent movement.',
  unlockLabel: 'Next phase unlocks',
  unlockDate: 'Sep 01, 2026',
};
